import React from "react";
import SectionHeader from "./SectionHeader";

const TREND = [
  [2019, 18],
  [2020, 26],
  [2021, 41],
  [2022, 63],
  [2023, 88],
  [2024, 104],
  [2025, 121],
];
const MAX = 121;

const FORUMS = [
  { name: "CESTAT", n: 142, split: [46, 38, 16] },
  { name: "High Courts", n: 231, split: [52, 30, 18] },
  { name: "Supreme Court", n: 17, split: [41, 47, 12] },
];
const OUTCOME = [
  { label: "In favour of taxpayer", cls: "vd-c-navy" },
  { label: "In favour of revenue", cls: "vd-c-amber" },
  { label: "Remanded", cls: "vd-c-grey" },
];

const MAP = [
  {
    sub: "Reasonable steps & supplier checks",
    tag: "CESTAT · 2019",
    name: "Palav Synthetics",
  },
  {
    sub: "Blocking credit under Rule 86A",
    tag: "Gujarat HC · 2022",
    name: "New Nalbandh Traders",
  },
  {
    sub: "Bona fide purchaser protection",
    tag: "Calcutta HC · 2021",
    name: "LGW Industries",
  },
];

const ALERT_DOTS = [
  { label: "Final hearing · HC", x: 88, y: 88, c: "#B4690E" },
  { label: "Listing · CESTAT", x: 72, y: 70, c: "#B4690E" },
  { label: "ITC ruling", x: 80, y: 40, c: "#1E3270" },
  { label: "CGST notification", x: 58, y: 58, c: "#8FA3D4" },
  { label: "Related matter", x: 30, y: 30, c: "#1E3270" },
  { label: "CBDT instruction", x: 14, y: 14, c: "#8FA3D4" },
];

const WATCH = [
  {
    k: "WEEK 1 · CGST",
    t: "Notification on credit restrictions",
    d: (
      <>
        Affects <strong>4 matters</strong> · high relevance
      </>
    ),
  },
  {
    k: "WEEK 2 · CBIC",
    t: "Circular clarifying adjudication timelines",
    d: (
      <>
        Affects <strong>2 matters</strong> · medium
      </>
    ),
  },
  {
    k: "WEEK 3 · CUSTOMS",
    t: "Tariff notification amending duty rates",
    d: (
      <>
        Affects <strong>1 matter</strong> · medium
      </>
    ),
  },
  {
    k: "WEEK 4 · CBDT",
    t: "Instruction on reassessment procedure",
    d: "No active matters affected",
  },
];

const Figure = ({ title, sub, foot, children }) => (
  <figure className="vd-figure">
    <figcaption>
      <div className="vd-figure__title">{title}</div>
      <div className="vd-figure__sub">{sub}</div>
    </figcaption>
    {children}
    {foot && <div className="vd-figure__foot">{foot}</div>}
  </figure>
);

const LegalInsights = () => (
  <section id="insights" className="vd-section">
    <div className="container">
      <div className="d-flex flex-wrap justify-content-between align-items-end gap-3 mb-4">
        <SectionHeader
          eyebrow="LEGAL INSIGHTS"
          title="Don’t just find the law. See the pattern."
          className="vd-section-head mb-0"
        >
          Every research question in Vedum comes with an insight view: how often
          the issue is litigated, which way forums lean, how authorities group
          together, and what needs your attention first.
        </SectionHeader>
        <span className="vd-badge">Sample insight · illustrative data</span>
      </div>

      <div className="vd-qbox">
        <span className="vd-eyebrow">QUESTION</span>
        <span className="fw-semibold">
          Can input tax credit be denied where the supplier is later found
          non-existent?
        </span>
      </div>

      <div className="row g-4">
        {/* 1. Issue trend */}
        <div className="col-lg-6">
          <Figure
            title="Issue trend"
            sub="Reported decisions on this issue, by year"
            foot={
              <>
                Volume has risen <strong>~7×</strong> since 2019 — a fast-moving
                issue where the latest ruling matters most.
              </>
            }
          >
            <div
              className="vd-trend"
              role="img"
              aria-label="Column chart: decisions rising from 18 in 2019 to 121 in 2025"
            >
              {TREND.map(([y, v], i) => (
                <div
                  key={y}
                  className={`vd-trend__col ${i === TREND.length - 1 ? "is-last" : ""}`}
                >
                  <span>{v}</span>
                  <span
                    className="vd-trend__bar"
                    style={{ height: Math.round((v / MAX) * 170) }}
                  />
                </div>
              ))}
            </div>
            <div className="vd-trend__years">
              {TREND.map(([y]) => (
                <span key={y}>{y}</span>
              ))}
            </div>
          </Figure>
        </div>

        {/* 2. Outcomes by forum */}
        <div className="col-lg-6">
          <Figure
            title="Outcomes by forum"
            sub="How decisions on this issue have gone at each level"
            foot="Know where the odds stand before you choose to appeal — and which forum’s reasoning to lead with."
          >
            <div className="vd-legend">
              {OUTCOME.map((o) => (
                <span key={o.label}>
                  <i className={`vd-dot ${o.cls}`} /> {o.label}
                </span>
              ))}
            </div>
            {FORUMS.map((f) => (
              <div className="vd-forum" key={f.name}>
                <div className="vd-forum__head">
                  <strong>{f.name}</strong>
                  <span>{f.n} decisions</span>
                </div>
                <div className="vd-stack">
                  {f.split.map((p, i) => (
                    <span
                      key={i}
                      className={OUTCOME[i].cls}
                      style={{ width: `${p}%` }}
                    >
                      {p}%
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </Figure>
        </div>

        {/* 3. Research map */}
        <div className="col-lg-6">
          <Figure
            title="Research map"
            sub="Authorities grouped by the sub-issue they decide"
            foot="See the structure of the argument at a glance — then pick the authorities for each limb of your reply."
          >
            <div className="vd-map">
              <div className="vd-map__root">
                ITC &amp; non-existent suppliers
              </div>
              {MAP.map((m) => (
                <div className="vd-map__branch" key={m.name}>
                  <div className="vd-map__sub">{m.sub}</div>
                  <div className="vd-map__leaf">
                    <b>{m.tag}</b>
                    {m.name}
                  </div>
                </div>
              ))}
            </div>
          </Figure>
        </div>

        {/* 4. Alert priority */}
        <div className="col-lg-6">
          <Figure
            title="Alert priority"
            sub="This week’s alerts by relevance to your matters and urgency"
          >
            <div className="d-flex gap-2">
              <span
                className="vd-axis"
                style={{
                  writingMode: "vertical-rl",
                  transform: "rotate(180deg)",
                }}
              >
                Urgency →
              </span>
              <div className="flex-grow-1">
                <div
                  className="vd-matrix"
                  role="img"
                  aria-label="Scatter of six alerts; two hearings are high relevance and high urgency"
                >
                  <span className="vd-matrix__zone" />
                  <span className="vd-matrix__zone-label">ACT NOW</span>
                  {ALERT_DOTS.map((d) => {
                    const right = d.x > 60;
                    return (
                      <span
                        key={d.label}
                        className="vd-matrix__dot"
                        style={{
                          bottom: `${d.y}%`,
                          [right ? "right" : "left"]:
                            `${right ? 100 - d.x : d.x}%`,
                          flexDirection: right ? "row-reverse" : "row",
                          transform: `translate(${right ? "7px" : "-7px"}, 7px)`,
                        }}
                      >
                        <i style={{ background: d.c }} />
                        {d.label}
                      </span>
                    );
                  })}
                </div>
                <div className="vd-axis text-center mt-2">
                  Relevance to your matters →
                </div>
              </div>
            </div>
            <div className="vd-legend mt-3 mb-0">
              <span>
                <i className="vd-dot vd-c-rust" /> Hearing
              </span>
              <span>
                <i className="vd-dot vd-c-navy" /> Ruling
              </span>
              <span>
                <i className="vd-dot vd-c-peri" /> Notification
              </span>
            </div>
          </Figure>
        </div>
      </div>

      {/* Regulatory watch */}
      <div className="vd-watch">
        <div className="d-flex flex-wrap justify-content-between mb-3">
          <div>
            <div className="vd-figure__title">Regulatory watch</div>
            <div className="vd-figure__sub mb-0">
              Notifications and circulars mapped to the matters they affect
            </div>
          </div>
          <span className="vd-badge align-self-start">Last 30 days</span>
        </div>
        <div className="row g-4">
          {WATCH.map((w) => (
            <div className="col-md-6 col-lg-3" key={w.k}>
              <div className="vd-watch__item">
                <div className="vd-eyebrow">{w.k}</div>
                <h5>{w.t}</h5>
                <p>{w.d}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <p className="vd-small-note">
        Insight panels above show illustrative sample data to demonstrate the
        product. Figures in your workspace are computed from the sources behind
        your research.
      </p>
    </div>
  </section>
);

export default LegalInsights;
